"use client";

import { useEffect } from "react";

import {
  OPPREF_COOKIE,
  appendOpprefToBookingHref,
  getCampaignBookingHref,
  hasEmailCampaignCookie,
  readCookie,
} from "@/lib/utils/bookingHref";

/**
 * Rewrites outbound booking links for two independent kinds of attribution:
 * visitors carrying the email-campaign cookie get the email booking agent key
 * in place of the default web one, and visitors who arrived from a ChatGPT ad
 * get the `oppref` token appended so the OpenAI pixel on Singenuity can credit
 * the booking to the ad. A visitor can carry either, both, or neither.
 *
 * This runs as one delegated listener rather than a per-link change because
 * booking URLs are hardcoded in roughly a dozen places across JSX, tour data
 * files, and JSON-LD. One listener covers all of them plus any link added
 * later, which per-call-site edits would silently miss — and it leaves the
 * JSON-LD alone, since structured data is not an anchor.
 *
 * Rewriting the href during the click is safe: the handler runs synchronously
 * before the browser reads the href to navigate. If anything here throws, the
 * link still works — the visit is just unattributed.
 */
const BookingLinkDecorator = () => {
  useEffect(() => {
    const decorate = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      if (!anchor?.href) return;

      const cookies = document.cookie;

      const withCampaign = getCampaignBookingHref({
        href: anchor.href,
        hasCampaign: hasEmailCampaignCookie(cookies),
      });

      const decorated = appendOpprefToBookingHref({
        href: withCampaign,
        oppref: readCookie(cookies, OPPREF_COOKIE),
      });

      if (decorated !== anchor.href) anchor.href = decorated;
    };

    // click covers left-click, keyboard Enter, and ctrl/cmd-click. auxclick
    // covers middle-click and contextmenu covers "Open link in new tab" from
    // the right-click menu — both open booking links in a new tab without ever
    // firing a normal click event.
    const EVENTS = ["click", "auxclick", "contextmenu"] as const;

    for (const name of EVENTS) {
      document.addEventListener(name, decorate, true);
    }

    return () => {
      for (const name of EVENTS) {
        document.removeEventListener(name, decorate, true);
      }
    };
  }, []);

  return null;
};

export default BookingLinkDecorator;
